import { HStack, Pressable, Text } from '@ayble-health/dashboard-components';
import { Box, useToken } from '@chakra-ui/react';
import { useUpdateMeetingStatus } from 'api/healthCoach/mutations/useUpdateMeetingStatus';
import AppointmentBlockBadge from 'components/ProviderHome/AppointmentBlockBadge';
import {
  APPT_DURATION_FALLBACK,
  HOUR_PX,
  lEFT_OFFSET,
} from 'components/ProviderHome/constants';
import { apptDidNotOccur } from 'components/ProviderHome/utils';
import { formatDateInBrowserTZ } from 'components/UserDashboard/utils';
import { CareMemberAppointment } from 'models/careMemberAppointment';
import { useNavigate } from 'react-router-dom';

type Appointment = CareMemberAppointment & {
  startMs: number;
  endMs: number;
  joinable: boolean;
};

type Props = {
  appt: Appointment;
  isNext: boolean;
  startCol: number;
  topOffset: number;
  totalCols: number;
  scrollRef?: React.RefObject<HTMLDivElement>;
};

export const AppointmentBlock = ({
  appt,
  isNext,
  startCol,
  topOffset,
  totalCols,
  scrollRef,
}: Props) => {
  const [ink, subtle] = useToken('colors', ['ink', 'subtle']);
  const navigate = useNavigate();
  const { mutation: updateMeetingMutation } = useUpdateMeetingStatus();
  const {
    is_locked: chartNoteLocked,
    is_signed: chartNoteSigned,
    note_url,
  } = appt.chart_notes?.[0] || {};

  const {
    duration,
    pm_status,
    meeting_date,
    meeting_type,
    client_confirmed,
    user: patient,
    healthie_appointment_id,
    joinable,
    rescheduled_for,
  } = appt;

  const patientDetailRoute = `/user-details/${patient.id}`;

  const confirmMeeting = async () => {
    await updateMeetingMutation.mutateAsync({
      meetingId: healthie_appointment_id,
      payload: { client_confirmed: true },
    });
  };

  const openChartNote = () => {
    if (note_url) {
      window.open(note_url, '_blank', 'noopener');
    }
  };

  const navigateToUserDetail = () => navigate(patientDetailRoute);

  const leftOffset = `calc(${lEFT_OFFSET}px + ${startCol} * (100% - ${lEFT_OFFSET}px) / ${totalCols})`;
  const colWidth = `calc((100% - ${lEFT_OFFSET}px) / ${totalCols})`;
  const height = ((duration ?? APPT_DURATION_FALLBACK) * HOUR_PX) / 60;
  const apptNotOccurred = apptDidNotOccur(pm_status, !!rescheduled_for);
  const borderLeft =
    isNext && !apptNotOccurred
      ? 'seafoam'
      : apptNotOccurred
      ? 'fuschia'
      : undefined;

  return (
    <Box
      ref={scrollRef}
      py={'4px'}
      px={'1px'}
      position={'absolute'}
      top={`${topOffset}px`}
      h={`${height}px`}
      left={leftOffset}
      width={colWidth}
      transition="filter 0.12s ease"
      _hover={{ filter: 'brightness(0.97)' }}
    >
      <Pressable h={'100%'} w={'100%'} href={patientDetailRoute}>
        <HStack
          px={3}
          alignItems={'center'}
          gap={2}
          rounded={'xl'}
          borderWidth={1}
          h={'100%'}
          bg={isNext ? '#E7F0F1' : 'canvas'}
          borderLeftColor={borderLeft}
          borderLeftWidth={borderLeft ? 3 : undefined}
          overflow={'hidden'}
        >
          {apptNotOccurred ? (
            <Box flexShrink={0} px={2} py="3px">
              <Text fontSize="xs" color="seafoam">
                {formatDateInBrowserTZ(meeting_date, 'h:mm A z')}
              </Text>
            </Box>
          ) : (
            <Box
              flexShrink={0}
              bg={'#E7F0F1'}
              borderRadius="6px"
              px={2}
              py="3px"
            >
              <Text fontSize="xs" fontWeight={600} color="seafoam">
                {formatDateInBrowserTZ(meeting_date, 'h:mm A z')}
              </Text>
            </Box>
          )}
          <Text fontWeight={600} fontSize="sm" color={ink}>
            {patient.first_name} {patient.last_name}
          </Text>
          <Text fontSize="xs" color={subtle}>
            {meeting_type}
          </Text>
          <Box
            ml={'auto'}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <AppointmentBlockBadge
              pmStatus={pm_status}
              clientConfirmed={client_confirmed}
              confirmMeeting={confirmMeeting}
              joinable={joinable}
              chartNoteLocked={chartNoteLocked}
              chartNoteSigned={chartNoteSigned}
              openChartNote={openChartNote}
              handleJoin={navigateToUserDetail}
              rescheduledFor={rescheduled_for}
            />
          </Box>
        </HStack>
      </Pressable>
    </Box>
  );
};
export default AppointmentBlock;
